import { DrawPaneComponent } from './../components/draw-pane/draw-pane.component';
import { Comparisons } from './comparisons';
import { AttributeId, AttributeFeatureType, ReservedAttributeIds, AttributeOptionId, InputValue, CheckboxValue } from './../types/project-config';
import { IDRegistry } from './id-registry';
import { IDKeyTypes, IDManager, ObjID } from './id-manager';
import { ConfigAttribute, ProjectConfig, SupplementalAttribute, AvailCriteria } from 'app/types/project-config';
import { EventEmitter } from '@angular/core';

export const { Points, Segments, Supplemental, AttributeOption, ...IDAttTypes } = IDKeyTypes;

export type FeatureData = {
  [key in AttributeId]?: AttributeOptionId | InputValue | CheckboxValue;
}

export class Project {
  lastTouch = new Date();
  idManager: IDManager = new IDManager();
  idRegistry: IDRegistry;
  config?: ProjectConfig;
  projInfo?: any;
  drawPane?: DrawPaneComponent;

  public drawPaneReady = new EventEmitter<DrawPaneComponent>();

  private _destroyed: boolean = false;
  public get destroyed(): boolean {
    return this._destroyed;
  }
  public get projectId(): number {
    return this.projInfo?.project_id;
  }

  public viewMode:'view' | 'edit' = 'view';

  public onDestroy = new EventEmitter<Project>();
  public onVertexSelection = new EventEmitter<ObjID>();
  public constructor(projInfo?: any) {
    if (!!projInfo) this.projInfo = projInfo;
    this.idRegistry = new IDRegistry(this.idManager);
    if (!!projInfo && !!projInfo.project_config) {
      this.SetProjectConfig(projInfo.project_config.config);
    }
    console.log(this);
  }
  public SetProjectConfig(config: ProjectConfig) {
    this.config = config;
    this.RegisterConfig();

  }
  public SetUI(drawPane: DrawPaneComponent) {
    this.drawPane = drawPane;
    this.drawPaneReady.emit(this.drawPane);
  }
  public CreateAttribute(type: IDKeyTypes): ConfigAttribute {
    if (!this.config) {
      throw new Error('attempting to create attribute before config set');
    }
    if (type < IDAttTypes.PointAttributes || type > IDAttTypes.SupplementalAttributes) {
      throw new Error('attempting to create attribute with unknown id type')
    }
    const attribute: ConfigAttribute = {
      id: this.idManager.ID(type),
      calc: 'static',
      input: 'input',
      label: 'New Attribute',
      default: '',
      displayAlways: true
    }
    switch (type) {
      case IDKeyTypes.PointAttributes:
        this.config.attributes.point.push(attribute);
        break;
      case IDKeyTypes.SegmentAttributes:
        this.config.attributes.segment.push(attribute);
        break;
      case IDKeyTypes.SupplementalAttributes:
        this.config.attributes.suppl.push(attribute);
        break;
    }
    this.idRegistry.RegisterID(attribute.id, attribute);

    return attribute;
  }
  public GetFeatureType(id: ObjID): AttributeFeatureType | false {
    const keyType: IDKeyTypes = this.idManager.IDType(id);
    switch (keyType) {
      case IDKeyTypes.PointPreset:
      case IDKeyTypes.PointAttributes:
      case IDKeyTypes.PointAttributeCondition:
      case IDKeyTypes.Points:
        return 'point';
      case IDKeyTypes.SegmentPreset:
      case IDKeyTypes.SegmentAttributeCondition:
      case IDKeyTypes.SegmentAttributes:
      case IDKeyTypes.Segments:
        return 'segment';
      case IDKeyTypes.Supplemental:
      case IDKeyTypes.SupplementalAttributes:
        return 'suppl';
      default:
        return false;

    }
  }
  public DeleteAttribute(attribute: ConfigAttribute | SupplementalAttribute) {
    if (!this.config) {
      return;
    }
    const type = this.idManager.IDType(attribute.id as ObjID);
    let typeString: string = '';
    switch (type) {
      case IDKeyTypes.PointAttributes:
        typeString = 'point';
        break;
      case IDKeyTypes.SegmentAttributes:
        typeString = 'segment';
        break;
      case IDKeyTypes.SupplementalAttributes:
        typeString = 'suppl';
        break;
    }
    if (typeString === '') {
      return;
    }
    this.config.attributes[typeString as AttributeFeatureType].every((value: ConfigAttribute, index: number, src: Array<ConfigAttribute>) => {
      if (value.id != attribute.id) return true;
      if (!this.config) return true;
      this.config.attributes[typeString as AttributeFeatureType].splice(index, 1);
      return false;
    });
  }
  Touch() {
    this.lastTouch = new Date();
  }
  public GetAttributeOrder(type: AttributeFeatureType): Array<AttributeId> {
    const attributes = this.config?.attributes[type];
    if (!attributes) return [];
    const order: Array<AttributeId> = [];
    for (const attribute of attributes) {
      order.push(attribute.id);
    }
    return order;
  }
  public GetAttributeById(id: AttributeId): ConfigAttribute | SupplementalAttribute | false {
    if (!this.config) return false;
    let featureType: AttributeFeatureType | undefined = undefined;
    let isStringId = (typeof id === 'string');
    if (isStringId) {
      if (!isNaN(Number(('' + id).substring(0, 1)))) {
        isStringId = false;
        id = Number(id);
      }
    }
    if (isStringId && ReservedAttributeIds.indexOf(id as string) > -1) {
      switch (id as string) {
        case 'pointName':
          featureType = 'point';
          break;
        case 'segmentName':
          featureType = 'segment';
          break;
        case 'segmentType':
          featureType = 'segment';
          break;
        case 'pointType':
        case 'control-point':
        case 'transition-point':
          featureType = 'point';
          break;
      }
    } else if (isStringId === true) {
      isStringId = false;
      const numericId = this.idManager.FromStringID(id as string);
      if (numericId < 0) {
        return false;
      }
      id = numericId;
    }
    if (this.idRegistry && !isStringId) {
      return this.idRegistry.Get(id as number);
    }
    const idKeyType: IDKeyTypes = this.idManager.IDType(id as number);
    switch (idKeyType) {
      case IDKeyTypes.PointAttributes:
        featureType = 'point';
        break;
      case IDKeyTypes.SegmentAttributes:
        featureType: 'segment';
        break;
      case IDKeyTypes.SupplementalAttributes:
        featureType: 'suppl';
        break;
    }
    if (!featureType) {
      return false;
    }
    for (const attribute of this.config.attributes[featureType]) {
      if (attribute.id === id) {
        return attribute;
      }
    }
    return false;
  }
  GetConditionLabel(condition: AvailCriteria): string {
    const attId = condition.attribute_id as ObjID;
    const attribute = this.GetAttributeById(attId);
    if (!attribute) return '';
    let label: string = attribute.label;
    switch (attribute.input) {
      case 'selector':
        if (!attribute.options) return 'invalid criteria';
        label += ' equals ';
        for (const option of attribute.options) {
          if (option.id == condition.option_id) {
            return label + option.label;
          }
        }
        return 'invalid criteria';
      case 'checkbox':
        if (typeof condition.value !== 'boolean') {
          return 'invalid criteria';
        }
        const value: boolean = condition.value as boolean;
        return label + (value === true) ? ' is checked' : ' is not checked';
      case 'input':
        if (!condition.comparison) return 'invalid criteria';
        if (!condition.value || ('' === condition.value)) return 'invalid criteria';
        return label + ' ' + Comparisons[condition.comparison].label + ' ' + condition.value;
    }
    return 'invalid criteria';
  }
  RegisterConfig() {
    if (!this.config) return;
    this.idRegistry.ResetConfigIds();
    this.idManager.ResetConfigIds();
    this.RegisterConfigAttributes(IDKeyTypes.PointAttributes);
    this.RegisterConfigAttributes(IDKeyTypes.SegmentAttributes);
    this.RegisterConfigAttributes(IDKeyTypes.SupplementalAttributes);
  }
  RegisterConfigAttributes(type: IDKeyTypes) {
    if (!(this.config && this.config.attributes)) return;
    let src: Array<ConfigAttribute | SupplementalAttribute> | undefined = undefined;
    switch (type) {
      case IDKeyTypes.PointAttributes:
        src = this.config.attributes.point;
        break;
      case IDKeyTypes.SegmentAttributes:
        src = this.config.attributes.segment;
        break;
      case IDKeyTypes.SupplementalAttributes:
        src = this.config.attributes.suppl;
        break;
    }
    if (src === undefined) return;
    for (const att of src) {
      if (att.label.trim().toLowerCase() === 'name') {
        if (type === IDKeyTypes.PointAttributes) {
          att.id = 'pointName';
        }
        if (type === IDKeyTypes.SegmentAttributes) {
          att.id = 'segmentName';
        }
      }
      this.idRegistry.RegisterID(att.id, att);
      if (!!att.options && att.options.length > 0) {
        for (const attOpt of att.options) {
          this.idRegistry.RegisterID(attOpt.id, attOpt);
        }
      }
      if (!!att.availability && att.availability.length > 0) {
        for (const cond of att.availability) {
          this.idRegistry.RegisterID(cond.id, cond);
        }
      }
    }

  }
  DeleteAvailCriteria(attribute: ConfigAttribute, criteria: AvailCriteria) {

  }
  GetConfigAttributes(featureType: AttributeFeatureType): Array<ConfigAttribute> {
    if (!this.config) return [];
    if (!this.config.attributes[featureType]) return []
    return this.config.attributes[featureType];
  }
  Destroy() {
    this._destroyed = true;
    this.onDestroy.emit(this);
  }
  Merge(project: Project) {
    for (let key in project) {
      if (!project.hasOwnProperty(key)) { return false; }
      if (this.hasOwnProperty(key)) {
        (this as any)[key] = (project as any)[key] as any;
      }
    }
    return this;
  }
}
